/**
 * Nominatim access, in one place.
 *
 * Extracted for issue #379: correcting a mis-dropped pin by dragging it needs
 * the same "turn a coordinate into a town and a country" call the add flow
 * has made since #58, and a second copy of the identifier, the headers and
 * the URL shape is exactly the kind of duplicate that drifts — the copy that
 * gets a policy fix and the copy that doesn't look identical in review.
 *
 * NOMINATIM USAGE POLICY (spec §4.5, issue #58 hard requirement): requests
 * must carry an identifying User-Agent, be debounced (never per-keystroke,
 * never per-pixel of a drag), and never fire on empty input. This module
 * owns the identifier; the CALLER owns the debounce, because only the caller
 * knows what "the user stopped" means on its surface — typing stopped in the
 * search box, the pin stopped moving on the map.
 */

// Browsers refuse to let JS set the `User-Agent` header on fetch/XHR — it's
// on the Fetch spec's forbidden-header list, so this is silently dropped by
// every real browser and only takes effect in fetch implementations that
// don't enforce that restriction (Node/undici test runners, some SSR
// setups). The mechanism that DOES work from a browser, and that
// Nominatim's usage policy explicitly accepts as an equivalent identifier,
// is a valid `Referer` — `referrerPolicy: "origin"` below guarantees our
// deployed origin is sent instead of being silently suppressed by a
// stricter page-level default.
export const NOMINATIM_UA =
  "CricketCompass/1.0 (+https://cricket-compass.pages.dev; grounds directory geocoding)";

export function nominatimFetch(url, signal) {
  return fetch(url, {
    signal,
    referrerPolicy: "origin",
    headers: {
      "Accept-Language": "en",
      "User-Agent": NOMINATIM_UA,
    },
  });
}

/**
 * `zoom=14` is the town/suburb level — the coarsest zoom that still names a
 * settlement. Deliberately not the default (18), which resolves a house
 * number: the two fields this fills are `city` and `country`, and asking for
 * a street address to throw it away sends a more precise coordinate than the
 * answer needs.
 */
export function reverseGeocodeUrl(lat, lng) {
  return `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=14&addressdetails=1`;
}

/**
 * The two fields the app actually asks for, out of Nominatim's address blob.
 *
 * Falls down the settlement ladder — `city` is absent for anywhere smaller
 * than a city, which in New Zealand is most cricket grounds. Returns `null`
 * for a field with no answer rather than `""`, so a caller can tell "the
 * lookup had nothing to say" from "the lookup said it is nowhere" and leave
 * whatever the user already typed alone.
 */
export function placeFromReverseGeocode(data) {
  const a = data?.address || {};
  return {
    city: a.city || a.town || a.village || a.municipality || a.county || null,
    country: a.country || null,
  };
}

/**
 * Best-effort city/country for a coordinate. Resolves to `null` on anything
 * that went wrong — offline, rate-limited, aborted, a body that isn't JSON —
 * because every caller's fallback is the same one: the fields are still
 * there and the user can type it.
 */
export async function reverseGeocodePlace(lat, lng, signal) {
  try {
    const resp = await nominatimFetch(reverseGeocodeUrl(lat, lng), signal);
    if (!resp.ok) return null;
    return placeFromReverseGeocode(await resp.json());
  } catch {
    return null;
  }
}

/**
 * Issue #757 §3 — the HELD path. A player who declines "Use my location"
 * still needs a coordinate (`grounds.latitude`/`longitude` are NOT NULL and
 * stay that way — no schema change, no coordinate-less ground), so the
 * town they type is turned INTO one, the same way `GroundSearchDialog`'s
 * search tab already turns a typed name into a coordinate: one Nominatim
 * `/search` call, through the same `nominatimFetch` identifier/policy this
 * module already owns. `limit=1` — this is a coordinate lookup, not a
 * picker; the caller has one text field, not a results list to choose from.
 */
export function forwardGeocodeUrl(query) {
  return `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=1`;
}

/**
 * The one field this needs beyond `reverseGeocodePlace`'s own shape: a
 * coordinate. `country` rides along when Nominatim's own address block
 * names one, so the save doesn't have to guess "Unknown" for a town the
 * lookup actually resolved. `null` — never a thrown error — on anything
 * that went wrong or found nothing, so the caller can say "couldn't place
 * that town" and point back at the location tap, per §3.
 */
export async function forwardGeocodePlace(query, signal) {
  const q = String(query ?? "").trim();
  if (!q) return null;
  try {
    const resp = await nominatimFetch(forwardGeocodeUrl(q), signal);
    if (!resp.ok) return null;
    const data = await resp.json();
    const first = Array.isArray(data) ? data[0] : null;
    if (!first) return null;
    const lat = parseFloat(first.lat);
    const lng = parseFloat(first.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, country: first.address?.country || null };
  } catch {
    return null;
  }
}
